import React from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';

/**
 * HOW A CALCULATED NUMBER WAS WORKED OUT.
 *
 * Pending, TAR, ARPU, SFR and ACR are not read from the database. They are worked
 * out from columns that are. Clicking one opens this: what it means, the formula,
 * the same formula with THIS PERSON'S OWN NUMBERS put in, and the answer.
 *
 * NO API CALL
 *
 * Every input is already on the row, so this reads what the table is holding
 * rather than asking the server again. That also guarantees the working shown is
 * the working behind the number on screen, and not a second opinion.
 *
 * The inputs are listed underneath with a reminder that most of them open a popup
 * of their own, so the trail can be followed all the way down to a payment.
 */

const inr = (value) =>
  '₹ ' + Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const num = (value) => Number(value || 0).toLocaleString('en-IN');

const pct = (value) => Number(value || 0).toFixed(2) + ' %';

/** What an undefined column reads as, the same word the table uses. */
const NOT_AGREED = 'N/A';

/**
 * One entry per calculated column.
 *
 *   meaning   one line of plain English, no jargon
 *   formula   the names, so it can be read without the numbers
 *   parts     the inputs, in the order they appear in the formula
 *   answer    how to show the result
 *   note      what the answer actually tells you
 *   opens     which inputs have a popup of their own
 */
const WORKINGS = {
  pending: {
    title: 'Pending',
    meaning: 'How much is still left to earn this month.',
    formula: 'Revenue Target − Revenue',
    parts: [
      { label: 'Revenue Target', field: 'revenueTarget', format: inr },
      { label: 'Revenue', field: 'revenue', format: inr, opens: true },
    ],
    sum: (row) => `${inr(row.revenueTarget)} − ${inr(row.revenue)}`,
    answer: (row) => inr(row.pending),
    note: (row) =>
      Number(row.pending) <= 0
        ? 'A minus figure means the target was beaten, which is why it shows green.'
        : 'Still short of the target, which is why it shows red.',
  },

  tar: {
    title: 'TAR · Target Achievement Rate',
    meaning: 'How much of the target was actually earned.',
    formula: 'Revenue ÷ Revenue Target × 100',
    parts: [
      { label: 'Revenue', field: 'revenue', format: inr, opens: true },
      { label: 'Revenue Target', field: 'revenueTarget', format: inr },
    ],
    sum: (row) => `${inr(row.revenue)} ÷ ${inr(row.revenueTarget)} × 100`,
    answer: (row) => pct(row.tar),
    note: () =>
      '100 % means the target was met exactly. Above 100 % means it was beaten.',
  },

  arpu: {
    title: 'ARPU · Average Revenue Per User',
    meaning: 'The average money received per student.',
    formula: 'Revenue ÷ Admissions',
    parts: [
      { label: 'Revenue', field: 'revenue', format: inr, opens: true },
      { label: 'Admissions', field: 'admissions', format: num, opens: true },
    ],
    sum: (row) => `${inr(row.revenue)} ÷ ${num(row.admissions)} students`,
    answer: (row) => inr(row.arpu),
    note: () =>
      'Money received this month divided by students admitted this month. A student ' +
      'admitted earlier who paid an instalment now raises it without adding to the ' +
      'admission count.',
  },

  sfr: {
    title: 'SFR · Slot Fill Rate',
    meaning: 'How much of the assessment target was done.',
    formula: 'Assessments ÷ Slot Target × 100',
    parts: [
      { label: 'Assessments', field: 'assessments', format: num, opens: true },
      { label: 'Slot Target', field: 'slotTarget', format: num },
    ],
    sum: (row) => `${num(row.assessments)} ÷ ${num(row.slotTarget)} slots × 100`,
    answer: (row) => pct(row.sfr),
    note: () =>
      'Measured against the SLOT target, never the revenue target. They are two ' +
      'different numbers that happen to live on the same row. The slot target is ' +
      'set on the Targets and Slots screen and is not shown as a column.',
  },

  acr: {
    title: 'ACR · Assessment Conversion Rate',
    meaning: 'How many assessments turned into admissions.',
    formula: 'Admissions ÷ Assessments × 100',
    parts: [
      { label: 'Admissions', field: 'admissions', format: num, opens: true },
      { label: 'Assessments', field: 'assessments', format: num, opens: true },
    ],
    sum: (row) => `${num(row.admissions)} ÷ ${num(row.assessments)} × 100`,
    answer: (row) => pct(row.acr),
    note: () =>
      'The two are counted in the same month but do not have to be the same people. ' +
      'A student assessed in March who paid in April counts as an admission here ' +
      'without an assessment behind it.',
  },

  total: {
    title: 'Total',
    meaning: 'The whole payout for the month.',
    formula: 'Incentive + Monthly Bonus + Daily Bonus',
    parts: [
      { label: 'Incentive', field: 'incentive', format: inr },
      { label: 'Monthly Bonus', field: 'monthlyBonus', format: inr },
      { label: 'Daily Bonus', field: 'dailyBonus', format: inr },
    ],
    sum: () => NOT_AGREED + ' + ' + NOT_AGREED + ' + ' + NOT_AGREED,
    answer: () => NOT_AGREED,
    note: () =>
      'All three parts are still to be defined with the database admin, and no ' +
      'table exists to read them from, so this stays empty.',
  },
};

export default function CalculationDialog({ open, onClose, person, field }) {
  const working = field ? WORKINGS[field] : null;
  if (!working || !person) return null;

  const missing = working.parts.filter(
    (part) => person[part.field] === null || person[part.field] === undefined,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {working.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {person.oga}
              {person.team ? ` · ${person.team}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Icon icon="mdi:close" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">{working.meaning}</Typography>

          {/* the formula, then the same formula with this row's own numbers. The
              heading avoids a possessive on the name: user.name is stored with a
              trailing space on some rows and nothing trims it, so "NAME 's" read
              wrongly. */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Formula
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {working.formula}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  With the numbers on this row
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {working.sum(person)}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Answer
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {working.answer(person)}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Where each number came from
            </Typography>
            <Stack spacing={0.5} mt={0.5}>
              {working.parts.map((part) => (
                <Stack
                  key={part.field}
                  direction="row"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Typography variant="body2">
                    {part.label}
                    {part.opens && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {'  '}· click that column to see the list behind it
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {person[part.field] === null || person[part.field] === undefined
                      ? NOT_AGREED
                      : part.format(person[part.field])}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {missing.length > 0 && (
            <Alert severity="warning" icon={<Icon icon="mdi:help-circle-outline" />}>
              {missing.map((m) => m.label).join(', ')}{' '}
              {missing.length === 1 ? 'is' : 'are'} not agreed yet, so this cannot
              be worked out.
            </Alert>
          )}

          <Alert severity="info" icon={<Icon icon="mdi:information-outline" />}>
            {working.note(person)}
          </Alert>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
